'use client';

import {
	type OnStepperFirstButtonClicked,
	useIntl,
	type UserProofOfAddressManualRequirementConfig,
	type UserRequirementStateStatus,
	useSetPageTitle
} from '@capsicum/common';
import { ModalService, Stepper } from '@capsicum/common-web';
import { useProofOfAddress } from '@capsicum/features/regulations/shared';
import { type Requirement, useUser } from '@capsicum/socios-core';
import { useEffect } from 'react';
import { FormProvider } from 'react-hook-form';
import styled from 'styled-components';

import { useIdentityVerificationModals } from '../../hooks/useIdentityVerificationModals';
import { CredentialGuardLoader } from '../CredentialGuardLoader';
import { ProcessingModal } from '../personal-verification/ProcessingModal';

import { Step1 } from './Step1';
import { Step2 } from './Step2';

type ProofOfAddressProps = {
	isForcedRequirement?: boolean;
	onDismiss: (callback?: () => void) => void;
	requirement: Requirement<UserProofOfAddressManualRequirementConfig>;
	status?: UserRequirementStateStatus;
};

export const ProofOfAddress = ({ isForcedRequirement, onDismiss, requirement, status }: ProofOfAddressProps) => {
	const { $t } = useIntl();
	const { refetchUser } = useUser();
	const { showIdentityVerificationRejectedTileModal } = useIdentityVerificationModals();

	const {
		disabledFirstButton,
		isReadonlyMode,
		isRequirementSubmissionLoading,
		proofOfAddressForm,
		requirementSubmissionIsSuccess,
		setIndex,
		setUploadedFiles,
		stepperRef,
		submit
	} = useProofOfAddress({ requirement, status });

	useSetPageTitle($t('kyc.level3Text'));

	const showUnderReviewModal = (shouldRefetchUser?: boolean) => {
		ModalService.addModal({
			detached: true,
			isClosable: !isForcedRequirement,
			onDismiss: () => {
				if (shouldRefetchUser) refetchUser();
				onDismiss();
			},
			renderComponent: ({ close }) => (
				<ProcessingModal
					close={close}
					hideCloseButton={isForcedRequirement}
					testID="proof-of-address-processing-modal"
				/>
			)
		});
	};

	const steps = [
		() => <Step1 allowChangeOfCountry={requirement.config.allowChangeOfCountry} readOnly={isReadonlyMode} />,
		() => <Step2 isLoading={isRequirementSubmissionLoading} onUploadFiles={setUploadedFiles} />
	];

	useEffect(() => {
		if (requirementSubmissionIsSuccess) {
			showUnderReviewModal(true);
		}
	}, [requirementSubmissionIsSuccess]);

	useEffect(() => {
		if (status === 'retry') {
			showIdentityVerificationRejectedTileModal();
		}

		if (status === 'under_review') {
			showUnderReviewModal();
		}
	}, [status]);

	const onFirstButtonClick: OnStepperFirstButtonClicked = (_, __) => onDismiss();

	if (requirementSubmissionIsSuccess || status === 'under_review') {
		return <CredentialGuardLoader />;
	}

	return (
		<S.Wrapper>
			<FormProvider {...proofOfAddressForm}>
				<Stepper
					customBackNavigation={onDismiss}
					disabledFirstButton={disabledFirstButton}
					firstButtonText={isReadonlyMode ? $t('global.button.back') : undefined}
					hiddenSecondButton={isReadonlyMode}
					isLoading={isRequirementSubmissionLoading}
					onlyButtonsNavigation={isForcedRequirement}
					ref={stepperRef}
					showStepsNavigation={!isReadonlyMode}
					steps={steps}
					testID="proof-of-address-stepper"
					onFirstButtonClick={isReadonlyMode ? onFirstButtonClick : undefined}
					onStepIndexChange={setIndex}
					onSubmitSteps={submit}
				/>
			</FormProvider>
		</S.Wrapper>
	);
};

namespace S {
	export const Wrapper = styled.div`
		padding: ${({ theme }) => theme.spacings.medium} 0;
		${({ theme }) => theme.breakpoints.queries.below.tabletSmallDesktop} {
			padding: ${({ theme }) => theme.spacings.medium};
		}
		width: 100%;
	`;
}
